import type { UserVolunteerRoleAttributes } from '@/server/models'

import WorldQ from '@/queries/WorldQ'
import { SQL, type SQLFragment } from '@/client/query'

import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import UserQ from './UserQ'
import VolunteerRoleQ from './VolunteerRoleQ'

const UserVolunteerRoleQ = {
  ...makeQueryModelV2<UserVolunteerRoleAttributes>('user_volunteer_role')([
    'id',
    'user_id',
    'volunteer_role_id',
    'world_id',
    'start_date',
    'end_date',
    'created_at',
    'updated_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    VolunteerRole: {
      query: VolunteerRoleQ,
      foreignKey: UserVolunteerRoleQ.volunteer_role_id,
      sourceKey: VolunteerRoleQ.id,
    },
    User: {
      query: UserQ,
      foreignKey: UserVolunteerRoleQ.user_id,
      sourceKey: UserQ.id,
    },
    World: {
      query: WorldQ,
      foreignKey: UserVolunteerRoleQ.world_id,
      sourceKey: WorldQ.id,
    },
  }),

  where: (
    {
      id,
      userId,
      volunteerRoleId,
      isActive,
      include = [],
      groupBy = null,
      order = null,
      paranoid = true,
    }: {
      id?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      volunteerRoleId?: number | null | SQLFragment<number>
      isActive?: boolean | null
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<UserVolunteerRoleAttributes> => SQL`
    FROM ${UserVolunteerRoleQ}
    ${includeAssociationsToSql(UserVolunteerRoleQ, include)}
    WHERE
        ${id != null ? SQL`${UserVolunteerRoleQ.id} = ${id} AND` : SQL``}
        ${userId != null ? SQL`${UserVolunteerRoleQ.user_id} = ${userId} AND` : SQL``}
        ${
          volunteerRoleId != null
            ? SQL`${UserVolunteerRoleQ.volunteer_role_id} = ${volunteerRoleId} AND`
            : SQL``
        }
        ${isActive != null ? SQL`${UserVolunteerRoleQ.isActive()} = ${isActive} AND` : SQL``}
        ${paranoid ? SQL`${UserVolunteerRoleQ.deleted_at} IS NULL AND` : SQL``}
        ${customWhere != null ? customWhere : SQL`TRUE`}
    ${queryGroupByToSQL(groupBy)}
    ${queryOrderToSQL(order)}
    `,

  isActive: (): SQLFragment<boolean> => SQL`(
    ${UserVolunteerRoleQ.start_date} < NOW()
    AND (${UserVolunteerRoleQ.end_date} IS NULL OR ${UserVolunteerRoleQ.end_date} > NOW())
  )`,
}

export default UserVolunteerRoleQ
